import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { firstValueFrom, of, Subject } from 'rxjs';
import { catchError, delay } from 'rxjs/operators';
import { NotificationService } from '@notification/notification.service';
import { StateService } from '@services/state.service';
import { isFunction, objectKeys } from '@util/utils';
import { MyLog } from '@common/common';

function tryJSON<T = any>(
  sub: Subject<T>,
  data: any,
  headers?: any
) {
  let obj;
  try {
    obj = JSON.parse(data);
  } catch (e) {
    obj = {
      success: false,
      error: 'Probleme beim Verarbeiten der Serverantwort'
    }
  }
  if (headers) {
    obj.headers = headers;
  }
  sub.next(obj);
  sub.complete();
}

@Injectable({
  providedIn: 'root'
})
export class DataService {

  private prefix = '';

  delays: any = {

  };

  constructor(
    private notificationService: NotificationService,
    private stateService: StateService,
    private http: HttpClient
  ) {

    if (window.location.hostname && window.location.hostname.indexOf('localhost') >= 0) {
      this.prefix = 'http://localhost:3080';
    }

    if (this.stateService.isDevMode()) {
      MyLog.log('LOC:', window.location);
      MyLog.log('Prefix:', this.prefix);
    }
  }

  setDelay(url: string, delay: number) {
    this.delays[url] = delay;
  }

  getDelay(url: string) {
    for (let attr in this.delays) {
      if (url.indexOf(attr) === 0) {
        return this.delays[attr];
      }
    }
    return 0;
  }

  toRequestUrl(url: string) {
    if (url.charAt(0) !== '/') {
      url = '/' + url;
    }
    return this.prefix + url;
  }

  commonOptions(): any {
    return {
      headers: {
        'Content-Type': 'application/json'
      },
      responseType: 'json'
    };
  }

  get(url: string, params: any = {}) {
    const reqDelay = this.getDelay(url);

    url = this.toRequestUrl(url);

    let options = this.commonOptions();

    if ((params instanceof HttpParams) || (objectKeys(params).length > 0)) {
      options.params = params;
    }

    const result = this.http.get<any>(url, options).pipe(
      catchError((error: any) => {
        this.notificationService.onServerGet(error);
        return of(null);
      })
    );

    if (reqDelay > 0) {
      return result.pipe(delay(reqDelay));
    }

    return result;
  }

  delete(url: string, data: any = {}, options?: any) {
    const reqDelay = this.getDelay(url);

    url = this.toRequestUrl(url);

    let reqOptions = this.commonOptions();

    if (options) {
      Object.assign(reqOptions, options);
    }

    const result = this.http.delete<any>(
      url,
      reqOptions
    ).pipe(
      catchError((error: any) => {
        this.notificationService.onServerPost(error);
        return of(null);
      })
    );

    if (reqDelay > 0) {
      return result.pipe(delay(reqDelay));
    }

    return result;
  }

  post(url: string, data: any, options?: any, delRT = false) {
    const reqDelay = this.getDelay(url);

    url = this.toRequestUrl(url);

    let reqOptions = this.commonOptions();

    if (options) {
      Object.assign(reqOptions, options);
    }
    if (delRT) {
      delete reqOptions.responseType;
    }

    const result = this.http.post<any>(
      url,
      data,
      reqOptions
    ).pipe(
      catchError((error: any) => {
        this.notificationService.onServerPost(error);
        return of(null);
      })
    );

    if (reqDelay > 0) {
      return result.pipe(delay(reqDelay));
    }

    return result;
  }

  postAndDownload(url: string, data: any, options?: any) {
    const reqDelay = this.getDelay(url);

    url = this.toRequestUrl(url);

    let reqOptions = this.commonOptions();

    if (options) {
      Object.assign(reqOptions, options);
    }

    const sub = new Subject();
    const obs = sub.asObservable();

    this.downloadAsFile(
      url,
      data,
    ).then((serverResponse: HttpResponse<any>) => {
      const downloadHeaders: any = {};

      if (serverResponse?.headers && isFunction(serverResponse.headers?.get)) {
        let n = serverResponse.headers.get('download-filename');
        if (n) {
          downloadHeaders.fileName = n;
        }
        n = serverResponse.headers.get('download-link');
        if (n) {
          downloadHeaders.link = n;
        }
      }

      const res = serverResponse.body;

      if (res instanceof Blob) {
        const r = res.slice(0, 2);
        r.text().then((t: any) => {
          if (t[0] === '{') {
            res.text().then((txtRes: any) => {
              tryJSON(sub, txtRes, downloadHeaders);
            });
          } else {
            sub.next({
              download: true,
              blob: res,
              headers: downloadHeaders
            });
            sub.complete();
          }
        });
      } else {
        tryJSON(sub, res, downloadHeaders);
      }
    });

    if (reqDelay > 0) {
      return obs.pipe(delay(reqDelay));
    }

    return obs;
  }

  public async downloadAsFile(
    url: any,
    reqPayload: any
  ): Promise<HttpResponse<any>> {

    //const file =
    return firstValueFrom( this.http
      .post<HttpResponse<any>>(url, reqPayload, {
        responseType: 'blob' as 'json',
        observe: "response" as "body"
      })
    );
      //.toPromise();
  }
  /*
    uploadFile(fileUpload: FileUpload, url: string) {
      const res = new FileUploadHanler(this.http);

      res.fileUpload = fileUpload;

      res.url = url;

      return res;
    }
    */
}
